// src/hooks/useIPLocation.js

import { useState, useEffect, useCallback } from 'react';
import ipLocationService from '../services/ipLocationService';

export const useIPLocation = (options = {}) => {
  const {
    autoLoad = true,
    maxRetries = 3,
    retryDelay = 1000,
    cacheEnabled = true
  } = options;

  const [position, setPosition] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [source, setSource] = useState(null);
  const [isFallback, setIsFallback] = useState(false);

  // Get location
  const getLocation = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let result;
      
      if (cacheEnabled) {
        // Try to get cached position first
        const cached = ipLocationService.getCurrentPosition();
        if (cached) {
          result = cached;
        } else {
          result = await ipLocationService.getLocationWithRetry(maxRetries, retryDelay);
        }
      } else {
        result = await ipLocationService.getLocationWithRetry(maxRetries, retryDelay);
      }

      setPosition(result);
      setSource(result.source || 'IP-based');
      setIsFallback(result.isFallback || false);
      
      return result;
    } catch (err) {
      const errorMessage = err.message || 'Failed to get location';
      setError(errorMessage);
      
      // Try fallback as last resort
      try {
        const fallback = ipLocationService.getFallbackLocation();
        setPosition(fallback);
        setSource('fallback');
        setIsFallback(true);
        return fallback;
      } catch (fallbackError) {
        // If even fallback fails
        setError('Unable to determine location');
        throw new Error('Unable to determine location');
      }
    } finally {
      setLoading(false);
    }
  }, [maxRetries, retryDelay, cacheEnabled]);

  // Refresh location (clear cache and fetch new)
  const refreshLocation = useCallback(async () => {
    ipLocationService.clearCache();
    return await getLocation();
  }, [getLocation]);

  // Auto-load on mount
  useEffect(() => {
    if (autoLoad) {
      getLocation().catch(err => {
        console.warn('Auto location load failed:', err.message);
      });
    }
  }, [autoLoad, getLocation]);

  // Format location for display
  const formatLocation = useCallback((location = position) => {
    return ipLocationService.formatLocation(location);
  }, [position]);

  // Get distance between locations
  const getDistance = useCallback((coord1, coord2) => {
    return ipLocationService.getDistance(coord1, coord2);
  }, []);

  return {
    position,
    error,
    loading,
    source,
    isFallback,
    getLocation,
    refreshLocation,
    formatLocation,
    getDistance,
    clearCache: ipLocationService.clearCache.bind(ipLocationService),
    isAvailable: ipLocationService.isLocationAvailable(),
    hasLocation: !!position,
    locationString: position ? formatLocation(position) : null
  };
};

export default useIPLocation;